import React, { useCallback, useMemo, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

// Components
import CardContent from "./CardContent";

// Utils
import { isAdmin } from "../../../utils/constants";
import { formatTimeFromNow } from "../../../utils";
import { useResponsive } from "../../../hooks/useResponsive";
import { useSetMultipleParams } from "../../../hooks/useSetMultipleParams";

function areBoardNamesEqual(prevBoardName, nextBoardName) {
  return (
    prevBoardName?.key === nextBoardName?.key &&
    prevBoardName?.is_owner === nextBoardName?.is_owner &&
    prevBoardName?.board_admin === nextBoardName?.board_admin &&
    prevBoardName?.board_permission?.is_admin ===
      nextBoardName?.board_permission?.is_admin &&
    prevBoardName?.board_permission?.can_update_status ===
      nextBoardName?.board_permission?.can_update_status &&
    prevBoardName?.board_permission?.can_reassign_user ===
      nextBoardName?.board_permission?.can_reassign_user
  );
}

function areProjectsEqual(prevProject, nextProject) {
  return (
    JSON.stringify(prevProject ?? null) === JSON.stringify(nextProject ?? null)
  );
}

function KanbanCard({
  project,
  onDragEnd,
  boardName,
  index,
  id,
  status,
  isTodo = false,
  style = false,
  isAssignedUser = false,
  isMyTaskCard = false,
  boardKey,
  isMyPlanner = false,
  openDetailCard = () => {},
}) {
  const { id: boardId } = useParams();
  const [searchParams] = useSearchParams();
  const [setMultipleSearchParams] = useSetMultipleParams();
  const [collapsed] = useState(true);
  const currentUser = useSelector((state) => state.users.logInUser);
  const queryClient = useQueryClient();
  const cachedData = queryClient.getQueryData(["boardName", boardId]);
  const [isEditing, setIsEditing] = useState(false);
  const isMobile = useResponsive("down", "md");
  const navigate = useNavigate();


  const {
    card,
    board_permission: projectBoardPermission,
    board_admin,
    board_name,
    key: boardUniqueKey,
  } = project || {};
  const {
    assigned_to_user,
    card_id,
    board_id,
    reporter,
    is_card_collaborator,
  } = card || {};
  const {
    first_name = "",
    last_name = "",
    user_id: assignedUserId,
  } = assigned_to_user || {};

  const tooltipName = useMemo(
    () => `${first_name} ${last_name}`,
    [first_name, last_name]
  );
  const name = useMemo(
    () => `${first_name[0] || ""}${last_name[0] || ""}`,
    [first_name, last_name]
  );
  const boardPermission = isMyTaskCard
    ? projectBoardPermission
    : boardName?.board_permission ?? {};
  const searchQueryString = useMemo(() => searchParams.toString(), [searchParams]);

  const handleDetails = useCallback((event) => {
    // Check if modifier keys are pressed (Cmd/Ctrl or Shift)
    const isModifierPressed = event.metaKey || event.ctrlKey || event.shiftKey;

    if (isMyTaskCard || isMyPlanner) {
      if (isModifierPressed) {
        const params = new URLSearchParams();
        params.set("card_id", card_id);
        params.set("brd_id", board_id);
        window.open(
          `${window.location.pathname}?${params.toString()}`,
          "_blank"
        );
        event.stopPropagation();
        return;
      }

      setMultipleSearchParams([
        { name: "card_id", value: card_id },
        { name: "brd_id", value: board_id },
      ]);
      openDetailCard?.();
    } else if (!isAssignedUser) {
      const url = `/board/${boardKey || boardUniqueKey}/${
        boardKey || boardUniqueKey
      }-${card_id}?${searchQueryString}`;

      if (isModifierPressed) {
        // Open in new tab/window
        window.open(url, "_blank");
        event.stopPropagation();
        return;
      }

      navigate(url);
    } else {
      event.stopPropagation();
    }
  }, [
    boardKey,
    boardUniqueKey,
    card_id,
    board_id,
    isAssignedUser,
    isMyPlanner,
    isMyTaskCard,
    navigate,
    openDetailCard,
    searchQueryString,
    setMultipleSearchParams,
  ]);

  const openSubtask = useCallback((event, id) => {
    event.preventDefault();
    event.stopPropagation();

    // Check if modifier keys are pressed (Cmd/Ctrl or Shift)
    const isModifierPressed = event.metaKey || event.ctrlKey || event.shiftKey;

    if (isMyTaskCard) {
      if (isModifierPressed) {
        // For new tab/window, construct the URL and open it
        const params = new URLSearchParams();
        params.set("card_id", id);
        params.set("brd_id", board_id);
        window.open(
          `${window.location.pathname}?${params.toString()}`,
          "_blank"
        );
        event.stopPropagation();
        return;
      }
      setMultipleSearchParams([
        { name: "card_id", value: id },
        { name: "brd_id", value: board_id },
      ]);
      openDetailCard?.();
    } else if (!isAssignedUser) {
      const url = `/board/${boardKey || boardUniqueKey}/${
        boardKey || boardUniqueKey
      }-${id}?${searchQueryString}`;

      if (isModifierPressed) {
        window.open(url, "_blank");
        event.stopPropagation();
        return;
      }
      navigate(url);
    } else {
      event.stopPropagation();
    }
  }, [
    boardKey,
    boardUniqueKey,
    board_id,
    isAssignedUser,
    isMyTaskCard,
    navigate,
    openDetailCard,
    searchQueryString,
    setMultipleSearchParams,
  ]);

  const { user_id: currentUserId } = currentUser || {};
  const { data: { data: { responseData: { owner_user_id } = {} } = {} } = {} } =
    cachedData || {};

  const checkOwnerOrAdmin = isMyTaskCard
    ? isAdmin() || currentUserId === board_admin || boardPermission?.is_admin
    : isAdmin() ||
      currentUserId === owner_user_id ||
      boardPermission?.is_admin ||
      cachedData?.data?.data?.responseData?.is_owner;

  const accessDragCard =
    !isMyTaskCard &&
    (checkOwnerOrAdmin ||
      currentUserId === assignedUserId ||
      currentUserId === reporter);

  const accessForEditCard = checkOwnerOrAdmin || currentUserId === reporter;

  const isAssignee = currentUserId === assignedUserId;

  const accessForAssign =
    checkOwnerOrAdmin ||
    currentUserId === reporter ||
    isAssignee ||
    is_card_collaborator;

  const accessForChangeStatus =
    isMyTaskCard &&
    (checkOwnerOrAdmin ||
      currentUserId === reporter ||
      currentUserId === assignedUserId);

  const openEditField = useCallback((e) => {
    e.stopPropagation();
    accessForEditCard && setIsEditing(true);
  }, [accessForEditCard]);

  const cardContentProps = useMemo(
    () => ({
      project,
      boardName,
      isAssignedUser,
      style,
      isMyTaskCard,
      accessForChangeStatus,
      onDragEnd,
      handleDetails,
      tooltipName,
      name,
      accessForAssign,
      isTodo,
      checkOwnerOrAdmin,
      currentUser,
      accessForEditCard,
      isMobile,
      openEditField,
      isAssignee,
      boardId,
      openSubtask,
      isEditing,
      setIsEditing,
      collapsed,
      formatTimeFromNow,
      boardKey,
      is_card_collaborator,
      boardPermission,
    }),
    [
      project,
      boardName,
      isAssignedUser,
      style,
      isMyTaskCard,
      accessForChangeStatus,
      onDragEnd,
      handleDetails,
      tooltipName,
      name,
      accessForAssign,
      isTodo,
      checkOwnerOrAdmin,
      currentUser,
      accessForEditCard,
      isMobile,
      openEditField,
      isAssignee,
      boardId,
      openSubtask,
      isEditing,
      collapsed,
      boardKey,
      is_card_collaborator,
      boardPermission,
    ]
  );

  return isMyTaskCard ? (
    <Tooltip title={board_name}>
      <CardContent {...cardContentProps} isMyPlanner={isMyPlanner} />
    </Tooltip>
  ) : (
    <Draggable
      key={id?.toString()}
      draggableId={card_id?.toString()}
      index={index}
      isDragDisabled={!accessDragCard  || (!project?.card?.assigned_to_user?.user_id==currentUser?.user_id && !boardPermission?.can_update_status && !isMyPlanner) || isMobile}
    >
      {(provided, snapshot) => (
        <CardContent
          {...cardContentProps}
          provided={provided}
          snapshot={snapshot}
          isMyPlanner={isMyPlanner}
        />
      )}
    </Draggable>
  );
}

function arePropsEqual(prevProps, nextProps) {
  return (
    areProjectsEqual(prevProps.project, nextProps.project) &&
    areBoardNamesEqual(prevProps.boardName, nextProps.boardName) &&
    prevProps.onDragEnd === nextProps.onDragEnd &&
    prevProps.index === nextProps.index &&
    prevProps.id === nextProps.id &&
    prevProps.status === nextProps.status &&
    prevProps.isTodo === nextProps.isTodo &&
    prevProps.style === nextProps.style &&
    prevProps.isAssignedUser === nextProps.isAssignedUser &&
    prevProps.isMyTaskCard === nextProps.isMyTaskCard &&
    prevProps.boardKey === nextProps.boardKey &&
    prevProps.isMyPlanner === nextProps.isMyPlanner &&
    prevProps.openDetailCard === nextProps.openDetailCard
  );
}

export default React.memo(KanbanCard, arePropsEqual);
